import React from 'react';
import { Button, OverlayTrigger, Popover, Row, Col, Form } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { Icon, Pill, Button as CanaveralButton } from '@storable/canaveral-components';
import Authorization from 'containers/Authorization';
import { BUILDER } from 'shared/abilities';
import { escapeAllSpecialCharacters } from '../../../../modules/search';
import style from './Search.scss';

class Search extends React.Component {
  static propTypes = {
    filters: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
        type: PropTypes.string,
      })),
    })),
    search: PropTypes.func.isRequired,
    updateFilters: PropTypes.func.isRequired,
    selectedFilters: PropTypes.object.isRequired,
    rowAuth: PropTypes.func,
    rightOfSearch: PropTypes.arrayOf(PropTypes.func),
    leftOfSearch: PropTypes.arrayOf(PropTypes.func),
    disabledRowCount: PropTypes.number,
    clearFilters: PropTypes.func,
  };

  static defaultProps = {
    filters: [],
    rightOfSearch: [],
    leftOfSearch: [],
  }

  constructor(props) {
    super(props);
    this.filterMenu = React.createRef();

    this.state = {
      newSelectedFilters: {},
      isPopoverVisible: false, // State to control popover visibility
    };
  }

  togglePopover = () => {
    this.setState(prevState => ({
      isPopoverVisible: !prevState.isPopoverVisible,
    }));
  }

  onClickCancel = () => {
    const { selectedFilters } = this.props;
    // this.filterMenu.current.hide();
    this.setState({ isPopoverVisible: false }); // Hide the popover
    this.setState({ newSelectedFilters: { ...selectedFilters } });
  }

  handleSearch = () => this.props.search();

  handleKeyPress = (e) => {
    const { keyCode } = e;
    const escapedSearchValue = escapeAllSpecialCharacters(e.target.value);
    this.handleFilterChange({ value: 'queryString' }, escapedSearchValue, () => {
      const { newSelectedFilters } = this.state;
      let timeout = null;

      if (keyCode === 13) {
        this.props.updateFilters({ ...newSelectedFilters });
        this.handleSearch();
        return;
      }

      // user has 1.5 seconds between keystrokes or search will execute
      clearTimeout(timeout);
      timeout = setTimeout(
        () => {
          this.props.updateFilters({ ...newSelectedFilters });
          this.handleSearch();
        }, 1500,
      );
    });
  }

  handleFilterChange = (opt, value, callback = () => {}) => {
    this.setState((previousState) => {
      const { newSelectedFilters } = previousState;
      const newFilters = { ...newSelectedFilters };

      if (opt.type) {
        const filterType = newFilters[opt.type] || [];
        const foundFilterIdx = filterType.findIndex(filter => filter.value === opt.value);

        if (foundFilterIdx > -1) {
          filterType[foundFilterIdx] = { ...filterType[foundFilterIdx], enabled: value };
        } else {
          filterType.push({ ...opt, enabled: value });
        }
        newFilters[opt.type] = [...filterType];
      } else {
        newFilters[opt.value] = value;
      }
      return { ...previousState, newSelectedFilters: newFilters };
    }, callback);
  }

  clearFilters = () => {
    const { clearFilters } = this.props;
    this.setState({ newSelectedFilters: {} });
    clearFilters();
  }

  clearFilter = (filter) => {
    const { updateFilters, selectedFilters } = this.props;

    const newFilters = { ...selectedFilters };

    if (filter.type) {
      const type = newFilters[filter.type];
      const idx = type.findIndex(opt => opt.value === filter.value);
      type[idx].enabled = false;
      newFilters[filter.type] = type;
    } else {
      delete newFilters[filter.value];
    }

    this.setState({ newSelectedFilters: newFilters });

    updateFilters(newFilters);
    this.handleSearch();
  }

  isChecked = (opt) => {
    const { newSelectedFilters } = this.state;
    if (opt.type) {
      const selectedType = newSelectedFilters[opt.type];
      if (!selectedType) return false;
      const selectedOpt = selectedType.find(filter => filter.value === opt.value);
      return selectedOpt && selectedOpt.enabled;
    }
    return newSelectedFilters[opt.value];
  }

  renderRightOfSearch = () => {
    const { rightOfSearch } = this.props;
    return rightOfSearch.map(option => option(this.props));
  }

  renderLeftOfSearch = () => {
    const { leftOfSearch } = this.props;
    return leftOfSearch.map(option => option(this.props));
  }

  renderFilterGroups() {
    const { filters } = this.props;

    return filters.map((filter) => {
      const options = filter.options.map(opt => (
        <Col xs={4} className={style.filterOption} key={opt.name}>
          <Authorization action="manage" subject={[BUILDER]}>
            {authorized => (
              <Form.Check
                id={opt.value}
                type="checkbox"
                inline
                className={opt.value === 'draft' && !authorized ? style.disabled : style.checkBox}
                checked={this.isChecked(opt)}
                value={opt.value}
                disabled={opt.value === 'draft' && !authorized}
                onChange={e => this.handleFilterChange(opt, e.target.checked)}
                label={opt.name}
              />
            )}
          </Authorization>
        </Col>
      ));

      return (
        <Row key={filter.name} className={style.filterGroup}>
          <Col xs={2}>
            <span className={style.filterName}>
              {filter.name}
            </span>
          </Col>
          <Col xs={10}>
            <Form.Group as={Row} key={filter.name} className={style.filterOptions}>
              {options}
            </Form.Group>
          </Col>
        </Row>
      );
    });
  }

  renderFilters() {
    const { newSelectedFilters } = this.state;
    const filterGroups = this.renderFilterGroups();
    return (
      <Popover id="TableFilters" className={style.filterContainer}>
        <div>
          {filterGroups}
        </div>
        <div className={style.searchFooter}>
          <Button
            className={`${style.applyButton} ${style.roundButton}`}
            variant="default"
            onClick={() => {
              this.props.updateFilters({ ...newSelectedFilters });
              this.handleSearch();
              this.setState({ isPopoverVisible: false }); // Hide the popover
            }}
          >
            Apply
          </Button>
          <Button
            variant="default"
            onClick={() => this.onClickCancel()}
            className={`${style.cancelButton} ${style.roundButton}`}
          >
            Cancel
          </Button>
        </div>
      </Popover>
    );
  }

  renderFilterPills() {
    const { selectedFilters } = this.props;

    const activeFilters = Object.entries({ ...selectedFilters }).filter(([filter, val]) => {
      if (filter !== 'queryString' && val) return true;
      return false;
    });

    const capitalise = string => string.charAt(0).toUpperCase() + string.slice(1);

    const filterPills = activeFilters.map(([filter, filterValue]) => {
      if (filterValue instanceof Array) {
        const enabledFilters = filterValue.filter(opt => opt.enabled);
        return enabledFilters.length === 0 ? null : enabledFilters.map(opt => (
          <Pill
            context="subtle"
            className={style.filterPills}
            onDismiss={() => this.clearFilter(opt)}
            key={opt.name}
            inline
          >
            {capitalise(opt.name)}
          </Pill>
        ));
      }
      return (
        <Pill
          context="subtle"
          className={style.filterPills}
          onDismiss={() => this.clearFilter({ value: filter })}
          key={filter}
          inline
        >
          {capitalise(filter)}
        </Pill>
      );
    }).filter(pill => !!pill);

    return (
      <React.Fragment>
        { filterPills }
        {filterPills.length > 0 &&
        <CanaveralButton
          className={style.clearAll}
          context="bare"
          borderless
          onClick={() => {
            this.clearFilters();
            this.handleSearch();
          }}
        >
           Clear All
        </CanaveralButton>}
      </React.Fragment>
    );
  }

  render() {
    const { filters } = this.props;
    const { newSelectedFilters: { queryString }, isPopoverVisible } = this.state;

    const filterButton = (
      <OverlayTrigger
        placement="bottom"
        ref={this.filterMenu}
        overlay={this.renderFilters()}
        trigger="click"
        show={isPopoverVisible} // Control visibility via state
        rootClose
        onHide={() => this.setState({ isPopoverVisible: false })} // Handle popover close
      >
        <Button
          variant="default"
          onClick={this.togglePopover}
          className={`${style.roundButton} ${style.filterButton}`}
        >
          <span className={style.filterIcon}>
            <Icon id="filter" label="filter pages" />
          </span>
          <span className={style.filterText}>
            Filter
          </span>
        </Button>
      </OverlayTrigger>
    );

    return (
      <React.Fragment>
        <Row>
          <Form.Group className={style.noBottom}>
            {this.renderLeftOfSearch()}
            <Row>
              <Col xs={4} className={style.searchBar}>
                <div className={style.searchIcon}>
                  <Icon id="search" label="search" />
                </div>
                <Form.Control
                  id="textInput"
                  type="text"
                  defaultValue={queryString}
                  placeholder="Search..."
                  className={style.searchInput}
                  onKeyUp={this.handleKeyPress}
                />
              </Col>
              {filters.length > 0 &&
              <Col xs={2}>
                {filterButton}
              </Col>
              }
            </Row>
            <div>
              {this.renderRightOfSearch()}
            </div>
          </Form.Group>

        </Row>
        <Row>
          <div>
            { this.renderFilterPills() }
          </div>
        </Row>
      </React.Fragment>
    );
  }
}

export default Search;
